import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import {
  CheckIcon,
  ChevronUpDownIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "@uidotdev/usehooks";
import { IContact } from "../../models/Contact";
import { useQuery } from "@tanstack/react-query";
import useApiHelper from "../../hooks/useApiHelper";
import { DefaultResponseWithData } from "../../models/system";
import { useVirtualizer } from "@tanstack/react-virtual";
import LoadingWheel from "../shared/LoadingWheel";
import { ContactCreateEditModal } from "./ContactCreateEditModal";
import apiConfig from "../../utils/apiConfig";

interface ContactComboboxProps {
  onContactSelect: (contact: IContact) => void;
  label?: string;
  rounded?: boolean;
}

export function ContactCombobox({ onContactSelect, label, rounded }: ContactComboboxProps) {
  const { get } = useApiHelper(apiConfig("ContactCombobox"));
  const [selectedContact, setSelectedContact] = useState<IContact>();
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newContactName, setNewContactName] = useState("");

  const debouncedSearch = useDebounce(searchTerm, 500);

  const NEW_CONTACT_OPTION = { id: "__new__", fullname: "" } as IContact;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["contacts", "search", debouncedSearch],
    queryFn: () =>
      get<DefaultResponseWithData<IContact[]>>(
        `/contact?searchTerm=${debouncedSearch}`
      ),
    staleTime: 1000 * 60 * 5, // 5 minutes
    keepPreviousData: true,
    enabled: debouncedSearch.length >= 3,
  });

  const contacts = useMemo(() => data?.data ?? [], [data]);

  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: contacts.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36, // Approximate height of each option
    overscan: 5, // Load a few extra items outside of the viewport
  });

  //Used to prevent scrollIntoView from being called on the options
  //This stops the options from flickering around when the user is scrolling
  useEffect(() => {
    const original = Element.prototype.scrollIntoView;
    Element.prototype.scrollIntoView = function () {/* no op */ };
    return () => {
      Element.prototype.scrollIntoView = original;
    };
  }, []);

  return (
    <div>
      <ContactCreateEditModal open={isModalOpen} setOpen={setIsModalOpen} newContactName={newContactName} />
      {label &&
        <label>
          <span className="text-sm font-medium text-gray-500">{label}</span>
        </label>
      }
      <Combobox
        as="div"
        by="id"
        value={selectedContact}
        onChange={(contact: IContact) => {
          if (contact?.id === "__new__") {
            setNewContactName(searchTerm);
            setIsModalOpen(true);
            return;
          }

          setSelectedContact(contact);
          onContactSelect(contact);
        }}
      >
        <div className="relative mt-1">
          <ComboboxInput
            className={`w-full py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 border-1 border-gray-300 ${rounded ? "rounded-md" : ""}`}
            onChange={(event) => setSearchTerm(event.target.value)}
            // onBlur={() => setSearchTerm("")}
            displayValue={(contact: IContact) => contact?.fullname}
            placeholder="Search contact by name or email..."
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            {debouncedSearch.length >= 3 && (isLoading || isFetching) ? (
              <div className="flex items-center justify-center h-5 w-5">
                <LoadingWheel />
              </div>
            ) : (
              <ChevronUpDownIcon
                className="size-5 text-gray-400"
                aria-hidden="true"
              />
            )}
          </ComboboxButton>

          <ComboboxOptions
            ref={parentRef}
            className="absolute z-30 mt-1 max-h-60 w-full overflow-auto bg-white py-1 text-base rounded-b-md ring-1 ring-gray-400 ring-opacity-5 focus:outline-none sm:text-sm"
          >
            {contacts && contacts.length > 0 && (
              <div
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  position: 'relative',
                  width: '100%',
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const contact = contacts[virtualRow.index];
                  return (
                    <div
                      key={contact.id}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      <ComboboxOption
                        key={contact.id}
                        value={contact}
                        className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-primary-600 data-[focus]:text-white data-[focus]:outline-none"
                      >
                        <span className="block truncate group-data-[selected]:font-semibold">
                          {contact.fullname} - {contact.email || "N/A"}
                        </span>

                        <span className="absolute inset-y-0 right-0 hidden items-center pr-4 text-primary-600 group-data-[selected]:flex group-data-[focus]:text-white">
                          <CheckIcon className="size-5" aria-hidden="true" />
                        </span>
                      </ComboboxOption>
                    </div>
                  );
                })
                }
              </div>
            )}


            {debouncedSearch.length >= 3 && contacts && (
              <ComboboxOption
                value={NEW_CONTACT_OPTION}
                className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 bg-gray-50 data-[focus]:bg-primary-600 data-[focus]:text-white"
              >
                <span className="flex items-center">
                  <PlusCircleIcon className="w-5 h-5 mr-2" />
                  <span className="truncate">
                    Add "{searchTerm}" as a new contact
                  </span>
                </span>
              </ComboboxOption>
            )}

          </ComboboxOptions>
        </div>
      </Combobox >
    </div >
  );
}
